import useMedicalRecordDetail from '@hooks/query/medicalRecords/useMedicalRecordDetail';
import { CheckmarkOutlined } from '@icon';
import { Loader, Modal, ModalProps } from '@ui';
import { rgba } from 'polished';
import React from 'react';
import { useTheme } from 'styled-components';
import { getOcrText } from './medical-record-detail.utils';

interface MedicalRecordDetailProps extends ModalProps {
  recordId?: string;
  userId: string;
  visible: boolean;
  onClose: () => void;
}

const MedicalRecordDetail: React.FunctionComponent<MedicalRecordDetailProps> = ({
  recordId,
  userId,
  ...rest
}) => {
  const [file, setFile] = React.useState(0);
  const [showOCR, setShowOCR] = React.useState(false);

  const theme = useTheme();
  const { data: item, isLoading } = useMedicalRecordDetail(userId, recordId);

  React.useEffect(() => {
    setFile(0);
    setShowOCR(false);
  }, [item]);

  const f = item?.medicalRecordFiles?.[file];
  const ocrText = React.useMemo(() => getOcrText(f), [f]);
  const paginationDisabled = item?.medicalRecordFiles ? item.medicalRecordFiles.length <= 1 : true;

  const renderDocument = () => {
    if (!f) {
      return null;
    }

    if (f.fileType.includes('pdf')) {
      return (
        <iframe
          src={f.path}
          style={{ width: showOCR ? 368 : 752, transition: 'all 0.2s', minHeight: '60vh' }}
        />
      );
    }

    if (f.fileType.includes('image')) {
      return (
        <div
          style={{
            maxWidth: showOCR ? 368 : 752,
            width: '100%',
            transition: 'all 0.2s',
            height: '60vh',
            overflow: 'auto',
          }}
        >
          <img src={f.path} alt="" style={{ width: '100%' }} key={f.path} />
        </div>
      );
    }

    return (
      <p
        key={f.path}
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(f.ocrText),
        }}
      />
    );
  };

  return (
    <Modal
      {...rest}
      title={item?.name ?? ''}
      destroyOnClose
      width={800}
      style={{ transition: 'all 0.2s' }}
      footer={
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {file + 1}/{item?.medicalRecordFiles?.length ?? 0}
          <Modal.Actions
            style={{ flex: 1 }}
            actions={[
              {
                label: 'OCR',
                key: 'OCR',
                variant: showOCR ? 'primary' : 'ghost',
                disabled: !ocrText,
                Icon: showOCR ? CheckmarkOutlined : undefined,
                onClick: () => {
                  setShowOCR((o) => !o);
                },
              },
              {
                label: 'Prev',
                key: 'Prev',
                variant: 'secondary',
                // @ts-expect-error
                leftArrow: true,
                disabled: paginationDisabled,
                onClick: () => {
                  setFile((f) => (f - 1) % (item?.medicalRecordFiles?.length ?? 0));
                },
              },
              {
                label: 'Next',
                key: 'next',
                variant: 'secondary', // @ts-expect-error
                rightArrow: true,
                disabled: paginationDisabled,
                onClick: () => {
                  setFile((f) => (f + 1) % (item?.medicalRecordFiles?.length ?? 0));
                },
              },
            ]}
          />
        </div>
      }
    >
      <div
        style={{
          display: 'flex',
          gap: showOCR ? 32 : 0,
          transition: 'all 0.2s',
          minHeight: '60vh',
          width: '100%',
          position: 'relative',
        }}
      >
        {renderDocument()}
        {ocrText ? (
          <p
            style={{
              width: showOCR ? 400 : 0,
              overflowY: 'scroll',
              alignSelf: 'stretch',
              maxHeight: '60vh',
              transition: 'all 0.2s',
            }}
            dangerouslySetInnerHTML={{
              __html: ocrText?.replace(/\/n/gm, '<br/>'),
            }}
          />
        ) : null}
        {isLoading ? (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              zIndex: 1,
              background: rgba(theme.color.surface.modal, 0.1),
            }}
          >
            <Loader loading variant="on-light" size="medium" />
          </div>
        ) : null}
      </div>
    </Modal>
  );
};

export default React.memo(MedicalRecordDetail);
